import { eur } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { NumberInput } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Users, CreditCard, Archive, Truck } from "lucide-react";
import { useI18n } from "@/i18n";

const slug = (s) => (s || "").replace(/\s/g, "");

export const OrgCard = ({ o, canEdit, priceLists, revenue, revYear, onEdit, onArchive, onDelete }) => { const { t } = useI18n(); return (
  <div className="rounded-md border border-border bg-card p-5 shadow-sm">
    <div className="flex justify-between items-start">
      <div>
        <div className="font-heading text-lg font-medium">{o.name}</div>
        {(o.is_supplier || o.is_customer) && (
          <div className="flex gap-1 mt-0.5">
            {o.is_customer && <span className="rounded-full bg-primary/10 text-primary text-[10px] px-2 py-0.5">{t("ord.customer")}</span>}
            {o.is_supplier && <span className="rounded-full bg-chart-4/15 text-chart-4 text-[10px] px-2 py-0.5">{t("common.supplier")}</span>}
          </div>
        )}
        <div className="text-xs text-muted-foreground whitespace-pre-line mt-1">{o.address || "—"}</div>
      </div>
      {canEdit && (
        <div className="flex gap-1">
          <Button size="sm" variant="outline" onClick={() => onEdit(o)} data-testid={`edit-org-${slug(o.name)}`}>{t("common.edit")}</Button>
          <Button size="sm" variant="ghost" title={t("common.archive")} onClick={() => onArchive(o)} data-testid={`arch-org-${slug(o.name)}`}><Archive className="h-4 w-4" /></Button>
          <Button size="sm" variant="ghost" disabled={o.has_sales} title={o.has_sales ? t("prod.hasSalesHint") : t("common.delete")} onClick={() => onDelete(o)} data-testid={`del-org-${slug(o.name)}`}>
            <Trash2 className={`h-4 w-4 ${o.has_sales ? "text-muted-foreground/40" : "text-destructive"}`} />
          </Button>
        </div>
      )}
    </div>
    <div className="mt-3 grid grid-cols-2 gap-2 text-sm text-muted-foreground">
      <div>{t("op.discount")}: <span className="text-foreground font-mono">{o.discount_percent || 0}%</span></div>
      <div>{t("ord.paymentTerms")}: <span className="text-foreground">{o.payment_terms || "—"}</span></div>
      <div className="col-span-2">{t("b2b.tabPriceLists")}: <span className="text-foreground">{priceLists.find((p) => p.id === o.price_list_id)?.name || "—"}</span></div>
      <div className="col-span-2 border-t border-border pt-2 mt-1 flex justify-between items-baseline">
        <span className="text-xs uppercase tracking-wider text-muted-foreground">{t("common.revenue")}{revYear !== "all" ? ` ${revYear}` : ""} (netto)</span>
        <span className="font-mono font-medium text-base text-foreground whitespace-nowrap" data-testid={`rev-${slug(o.name)}`}>{eur(revenue || 0)}</span>
      </div>
      {o.shipping_address && (
        <div className="col-span-2 border-t border-border pt-2 mt-1">
          <div className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1 mb-1"><Truck className="h-3 w-3" /> {t("ord.shippingAddress")}</div>
          <div className="text-foreground whitespace-pre-line text-xs" data-testid={`ship-addr-${slug(o.name)}`}>{o.shipping_address}</div>
          {o.shipping_contact && <div className="text-xs mt-0.5">z.H. {o.shipping_contact}</div>}
        </div>
      )}
    </div>
    {(o.contacts || []).length > 0 && (
      <div className="mt-3 border-t border-border pt-2">
        <div className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1 mb-1"><Users className="h-3 w-3" /> {t("ord.contactPerson")}</div>
        {o.contacts.map((c, i) => (
          <div key={`${c.name}-${c.email}-${i}`} className="text-sm">
            {[c.salutation, c.name].filter(Boolean).join(" ")}
            {c.role && <span className="text-muted-foreground"> · {c.role}</span>}
            {c.email && <span className="text-muted-foreground"> · {c.email}</span>}
            {c.phone && <span className="text-muted-foreground"> · {c.phone}</span>}
            {c.fax && <span className="text-muted-foreground"> · Fax {c.fax}</span>}
          </div>
        ))}
      </div>
    )}
    {o.bank?.iban && (
      <div className="mt-3 border-t border-border pt-2 text-sm">
        <div className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1 mb-1"><CreditCard className="h-3 w-3" /> IBAN</div>
        <div className="font-mono text-xs">{o.bank.account_holder} · {o.bank.bank_name}</div>
        <div className="font-mono text-xs">IBAN {o.bank.iban} {o.bank.bic && `· BIC ${o.bank.bic}`}</div>
      </div>
    )}
  </div>
); };

const ContactRows = ({ f, setF }) => {
  const { t } = useI18n();
  const add = () => setF({ ...f, contacts: [...f.contacts, { _k: crypto.randomUUID(), name: "", salutation: "", role: "", email: "", phone: "", fax: "" }] });
  const upd = (i, k, v) => { const c = [...f.contacts]; c[i] = { ...c[i], [k]: v }; setF({ ...f, contacts: c }); };
  const rm = (i) => setF({ ...f, contacts: f.contacts.filter((_, j) => j !== i) });
  return (
    <div className="mt-2">
      <div className="flex items-center justify-between mb-2">
        <Label className="flex items-center gap-1.5"><Users className="h-4 w-4" /> {t("ord.contactPerson")}</Label>
        <Button size="sm" variant="outline" onClick={add} data-testid="add-contact-btn"><Plus className="h-3.5 w-3.5 mr-1" /> {t("ord.contactPerson")}</Button>
      </div>
      <div className="space-y-2">
        {f.contacts.length > 0 && (
          <div className="grid grid-cols-12 gap-2 px-1 text-[11px] uppercase tracking-wider text-muted-foreground" data-testid="contact-headers">
            <div className="col-span-2">{t("org.salutation")}</div><div className="col-span-3">{t("common.name")}</div><div className="col-span-3">{t("login.email")}</div><div className="col-span-2">{t("common.phone")}</div><div className="col-span-1">{t("common.fax")}</div><div className="col-span-1"></div>
          </div>
        )}
        {f.contacts.map((c, i) => (
          <div key={c._k || i} className="grid grid-cols-12 gap-2 items-center">
            <select className="col-span-2 h-9 rounded-md border border-input bg-background px-2 text-sm"
              value={c.salutation || ""} onChange={(e) => upd(i, "salutation", e.target.value)}
              data-testid={`contact-salutation-${i}`}>
              <option value="">{t("org.salutationNone")}</option>
              <option value="Frau">{t("org.salutationMs")}</option>
              <option value="Herr">{t("org.salutationMr")}</option>
            </select>
            <Input className="col-span-3 h-9" placeholder={t("common.name")} value={c.name || ""} onChange={(e) => upd(i, "name", e.target.value)} data-testid={`contact-name-${i}`} />
            <Input className="col-span-3 h-9" placeholder={t("login.email")} value={c.email || ""} onChange={(e) => upd(i, "email", e.target.value)} />
            <Input className="col-span-2 h-9" placeholder={t("common.phone")} value={c.phone || ""} onChange={(e) => upd(i, "phone", e.target.value)} />
            <Input className="col-span-1 h-9" placeholder={t("common.fax")} value={c.fax || ""} onChange={(e) => upd(i, "fax", e.target.value)} data-testid={`contact-fax-${i}`} />
            <Button className="col-span-1" size="icon" variant="ghost" onClick={() => rm(i)} data-testid={`contact-remove-${i}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
          </div>
        ))}
        {f.contacts.length === 0 && <p className="text-xs text-muted-foreground">{t("common.none")}</p>}
      </div>
    </div>
  );
};

const BankFields = ({ f, setF }) => { const { t } = useI18n(); return (
  <div className="mt-3">
    <Label className="flex items-center gap-1.5 mb-2"><CreditCard className="h-4 w-4" /> IBAN</Label>
    <div className="grid grid-cols-2 gap-3">
      <Input placeholder={t("bank.holder")} value={f.bank.account_holder} onChange={(e) => setF({ ...f, bank: { ...f.bank, account_holder: e.target.value } })} data-testid="bank-holder" />
      <Input placeholder={t("bank.bank")} value={f.bank.bank_name} onChange={(e) => setF({ ...f, bank: { ...f.bank, bank_name: e.target.value } })} />
      <Input placeholder="IBAN" value={f.bank.iban} onChange={(e) => setF({ ...f, bank: { ...f.bank, iban: e.target.value } })} data-testid="bank-iban" />
      <Input placeholder="BIC" value={f.bank.bic} onChange={(e) => setF({ ...f, bank: { ...f.bank, bic: e.target.value } })} />
    </div>
  </div>
); };

export const OrgFormDialog = ({ open, setOpen, editId, f, setF, priceLists, onSave }) => { const { t } = useI18n(); return (
  <Dialog open={open} onOpenChange={setOpen}>
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader><DialogTitle>{editId ? t("ord.organization") : t("ord.organization")}</DialogTitle></DialogHeader>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2"><Label>{t("common.name")} *</Label><Input data-testid="org-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1.5" /></div>
        <div><Label>{t("tc.taxNumber")}</Label><Input value={f.vat_id} onChange={(e) => setF({ ...f, vat_id: e.target.value })} className="mt-1.5" /></div>
        <div><Label>{t("taxc.customerCountry")}</Label>
          <Input value={f.country_code || ""} maxLength={2} placeholder="DE" data-testid="org-country"
            onChange={(e) => setF({ ...f, country_code: e.target.value.toUpperCase().slice(0, 2) })} className="mt-1.5" />
          <p className="mt-1 text-xs text-muted-foreground">{t("taxc.countryMissing")}</p>
        </div>
        <div><Label>{t("dl.documentLanguage")}</Label>
          <select value={f.document_language || ""} data-testid="org-doc-language"
            onChange={(e) => setF({ ...f, document_language: e.target.value })}
            className="mt-1.5 h-10 w-full rounded-md border border-input bg-background px-3 text-sm">
            <option value="">{t("dl.tenantDefault")}</option>
            {["de", "en", "es", "fr", "it", "nl", "pl", "pt", "ru", "el", "sv", "no"].map((l) => (
              <option key={l} value={l}>{l.toUpperCase()}</option>
            ))}
          </select>
          <p className="mt-1 text-xs text-muted-foreground">{t("dl.frozenHint")}</p>
        </div>
        <div><Label>{t("op.discount")} %</Label><NumberInput testId="org-discount" value={f.discount_percent} onChange={(val) => setF({ ...f, discount_percent: val })} className="mt-1.5" /></div>
        <div><Label>{t("ord.paymentTerms")}</Label><Input value={f.payment_terms} onChange={(e) => setF({ ...f, payment_terms: e.target.value })} className="mt-1.5" /></div>
        <div><Label>{t("b2b.tabPriceLists")}</Label>
          <Select value={f.price_list_id || "none"} onValueChange={(v) => setF({ ...f, price_list_id: v === "none" ? "" : v })}>
            <SelectTrigger className="mt-1.5" data-testid="org-pricelist"><SelectValue placeholder={t("common.none")} /></SelectTrigger>
            <SelectContent><SelectItem value="none">{t("common.none")}</SelectItem>{priceLists.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="col-span-2"><Label>{t("od.address")}</Label><Textarea value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} className="mt-1.5" /></div>
        <div className="col-span-2"><Label>{t("ord.shippingAddress")} ({t("common.optional")})</Label><Textarea value={f.shipping_address} onChange={(e) => setF({ ...f, shipping_address: e.target.value })} className="mt-1.5"  data-testid="org-shipping-address" /></div>
        <div className="col-span-2"><Label>{t("ord.contactPerson")} ({t("common.optional")})</Label><Input value={f.shipping_contact} onChange={(e) => setF({ ...f, shipping_contact: e.target.value })} className="mt-1.5"  data-testid="org-shipping-contact" /></div>
        <div className="col-span-2"><Label>Website</Label><Input value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} className="mt-1.5" data-testid="org-website" /></div>
      </div>
      <ContactRows f={f} setF={setF} />
      <BankFields f={f} setF={setF} />
      <DialogFooter><Button data-testid="save-org-btn" onClick={onSave} disabled={!f.name}>{t("common.save")}</Button></DialogFooter>
    </DialogContent>
  </Dialog>
); };
